import { useState } from 'react';
import { Modal, Form, Input, Checkbox, InputNumber, App } from 'antd';
import { useCreateLanguage } from '@/hooks/useLanguages';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (code: string, runAiTranslate: boolean) => void;
}

export function AddLanguageModal({ open, onClose, onCreated }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{
    code: string;
    name_native: string;
    sort_order: number;
    enabled: boolean;
    run_ai: boolean;
  }>();
  const mutation = useCreateLanguage();
  const [busy, setBusy] = useState(false);

  async function submit() {
    const vals = await form.validateFields();
    setBusy(true);
    try {
      await mutation.mutateAsync({
        code: vals.code.trim(),
        name_native: vals.name_native.trim(),
        sort_order: vals.sort_order,
        enabled: vals.enabled,
      });
      message.success(`Language ${vals.code} created`);
      onCreated(vals.code, vals.run_ai);
      form.resetFields();
      onClose();
    } catch (e) {
      message.error(`Create failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Add language"
      onCancel={onClose}
      onOk={submit}
      okText="Create"
      confirmLoading={busy}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ sort_order: 99, enabled: true, run_ai: true }}
      >
        <Form.Item
          label="BCP-47 code"
          name="code"
          extra="e.g. vi, es, pt-BR, ko"
          rules={[
            {
              required: true,
              pattern: /^[a-z]{2,3}(-[A-Z][a-zA-Z]{1,7})?$/,
              message: 'invalid BCP-47',
            },
          ]}
        >
          <Input placeholder="vi" autoFocus />
        </Form.Item>
        <Form.Item
          label="Native name"
          name="name_native"
          extra="how speakers of the language write its name"
          rules={[{ required: true }]}
        >
          <Input placeholder="Tiếng Việt" />
        </Form.Item>
        <Form.Item label="Sort order" name="sort_order">
          <InputNumber min={0} max={9999} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="enabled" valuePropName="checked">
          <Checkbox>Enable immediately</Checkbox>
        </Form.Item>
        <Form.Item name="run_ai" valuePropName="checked">
          <Checkbox>Open AI Translate after create</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}
